import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, ChevronDown } from 'lucide-react';
import api from '../../utils/axios';

const EnquiryModal = ({ isOpen, onClose, initialProgram = null }) => {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', college: '', program: '' });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [colleges, setColleges] = useState([]);
    const [filteredColleges, setFilteredColleges] = useState([]);
    const [allPrograms, setAllPrograms] = useState([]);

    useEffect(() => {
        fetchColleges();
    }, []);

    useEffect(() => {
        if (isOpen && initialProgram && colleges.length > 0) {
            const programName = typeof initialProgram === 'string' ? initialProgram : (initialProgram?.title || '');
            const collegeNames = typeof initialProgram === 'object' ? (initialProgram?.colleges || (initialProgram?.college ? [initialProgram.college] : [])) : [];

            setFormData(prev => ({ ...prev, program: programName }));

            // Filter colleges that offer this program OR are in the collegeNames array
            const filtered = colleges.filter(c => {
                const matchesProgram = programName && c.programs && c.programs.some(p => p.toLowerCase().includes(programName.toLowerCase()));
                const matchesCollegeArray = collegeNames.length > 0 && collegeNames.some(name => c.name.toLowerCase().includes(name.toLowerCase()));
                return matchesProgram || matchesCollegeArray;
            });

            // If we have filtered results, use them, otherwise show all
            const displayColleges = filtered.length > 0 ? filtered : colleges;
            setFilteredColleges(displayColleges);

            // Auto-select the first matching college
            if (displayColleges.length > 0) {
                const targetCollege = filtered.length > 0 ? filtered[0] : colleges[0];
                setFormData(prev => ({
                    ...prev,
                    college: targetCollege.name,
                    program: programName || (targetCollege.programs?.[0] || targetCollege.name)
                }));
            }

            // Build unique programs list
            const programs = Array.from(new Set(colleges.flatMap(c => c.programs || [])));
            setAllPrograms(programs);
        } else if (isOpen && colleges.length > 0) {
            // Find all unique programs
            const programs = Array.from(new Set(colleges.flatMap(c => c.programs || [])));
            setAllPrograms(programs);

            const initialProg = formData.program || programs[0] || '';
            const filtered = colleges.filter(c => c.programs && c.programs.includes(initialProg));
            setFilteredColleges(filtered);

            if (filtered.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    program: initialProg,
                    college: prev.college && filtered.some(c => c.name === prev.college) ? prev.college : filtered[0].name
                }));
            }
        }
    }, [isOpen, initialProgram, colleges]);

    const fetchColleges = async () => {
        try {
            const res = await api.get('/colleges/active');
            const data = res.data.data;
            setColleges(data);
            
            // Build unique programs list initially
            const programs = Array.from(new Set(data.flatMap(c => c.programs || [])));
            setAllPrograms(programs);
            setFilteredColleges(data);
        } catch (err) {
            console.error('Failed to fetch colleges', err);
        }
    };

    const handleProgramChange = (e) => {
        const selectedProg = e.target.value;
        const filtered = colleges.filter(c => c.programs && c.programs.includes(selectedProg));
        setFilteredColleges(filtered);
        
        setFormData(prev => ({
            ...prev,
            program: selectedProg,
            college: filtered.length > 0 ? filtered[0].name : ''
        }));
    };

    const handleCollegeChange = (e) => {
        const selectedCol = e.target.value;
        setFormData(prev => ({
            ...prev,
            college: selectedCol
        }));
    };

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/leads/submit', { ...formData, leadType: 'Admission' });
            setSubmitted(true);
            setFormData({ name: '', email: '', phone: '', college: formData.college, program: formData.program });
            setTimeout(() => {
                setSubmitted(false);
                onClose();
            }, 3000);
        } catch (err) {
            alert('Submission failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-[#003366]/90 backdrop-blur-sm" onClick={onClose}></div>
            <div className="bg-white max-w-lg w-full rounded-sm shadow-2xl overflow-hidden relative z-10 animate-scaleIn">
                <div className="bg-[#cc0000] p-6 text-white flex justify-between items-center">
                    <h3 className="text-xl font-black uppercase tracking-wider">Admission Inquiry</h3>
                    <button onClick={onClose} className="hover:rotate-90 transition-transform"><X size={24} /></button>
                </div>
                
                <div className="p-10">
                    {submitted ? (
                        <div className="text-center py-10 space-y-4">
                            <CheckCircle2 size={64} className="text-green-500 mx-auto" strokeWidth={3} />
                            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Applied Successfully!</h2>
                            <p className="text-slate-500 font-bold uppercase text-[10px] tracking-widest">A counselor will be in touch shortly.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <input 
                                    type="text" required placeholder="STUDENT FULL NAME"
                                    className="w-full border-b-2 border-slate-200 py-3 text-xs font-black placeholder-slate-300 outline-none focus:border-[#cc0000] tracking-widest uppercase"
                                    value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <input 
                                    type="email" required placeholder="EMAIL ADDR"
                                    className="w-full border-b-2 border-slate-200 py-3 text-xs font-black placeholder-slate-300 outline-none focus:border-[#cc0000] tracking-widest uppercase"
                                    value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                                />
                                <input 
                                    type="tel" required placeholder="MOBILE NO"
                                    className="w-full border-b-2 border-slate-200 py-3 text-xs font-black placeholder-slate-300 outline-none focus:border-[#cc0000] tracking-widest uppercase"
                                    value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                                />
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[8px] font-black text-slate-400 mb-1 uppercase tracking-widest">Select Program</label>
                                    <div className="relative">
                                        <select 
                                            className="w-full appearance-none border-b-2 border-slate-200 py-2 pr-8 text-xs font-black text-slate-700 outline-none focus:border-[#cc0000] tracking-normal bg-transparent"
                                            value={formData.program} onChange={handleProgramChange}
                                        >
                                            {allPrograms.length > 0 ? (
                                                allPrograms.map((p, idx) => (
                                                    <option key={idx} value={p}>{p}</option>
                                                ))
                                            ) : (
                                                <option value="">No Programs Available</option>
                                            )}
                                        </select>
                                        <ChevronDown size={14} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[8px] font-black text-slate-400 mb-1 uppercase tracking-widest">Select College</label>
                                    <div className="relative">
                                        <select 
                                            className="w-full appearance-none border-b-2 border-slate-200 py-2 pr-8 text-xs font-black text-slate-700 outline-none focus:border-[#cc0000] tracking-normal bg-transparent"
                                            value={formData.college} onChange={handleCollegeChange}
                                        >
                                            {filteredColleges.length > 0 ? (
                                                filteredColleges.map((c, idx) => (
                                                    <option key={idx} value={c.name}>{c.name}</option>
                                                ))
                                            ) : (
                                                <option value="">No Matching Colleges</option>
                                            )}
                                        </select>
                                        <ChevronDown size={14} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>
                            </div>
                            <button 
                                type="submit" disabled={loading}
                                className="w-full bg-[#cc0000] text-white py-4 font-black uppercase text-[12px] tracking-[4px] shadow-xl hover:bg-[#003366] transition-all disabled:opacity-50"
                            >
                                {loading ? 'SUBMITTING...' : 'SUBMIT NOW'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EnquiryModal;
